// اجزای مشترک AP-CMP-01 (SCR-STAFF-800، 802، 806): کلیدهای Query، فهرست‌های شمارشی، رنگ وضعیت، انتخاب ارائه‌دهنده با جستجوی سروری و نمایش محاسبه.
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type {
  CmpCommissionTiming,
  CmpCompensationBasis,
  CmpStatementPeriod,
  CmpAgreementStatus,
  CmpAgreementType,
  CmpFixedChargeKind,
  CmpRuleScope,
  CmpSettlementMethod,
  CmpShareBasis,
  CmpShareEntryKind,
  CmpShareEntryStatus,
  CmpShareEntryView,
  CmpShareMethod,
  CmpShareReportGroup,
  CmpShareRole,
  CmpShareSourceKind,
} from '@phoenix/api-client';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, latinDigits, useMoneyFormatter, type StatusTone } from '@phoenix/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

export const AGREEMENTS_PATH = '/compensation/agreements';
export const LEDGER_PATH = '/compensation/ledger';
export const REPORT_PATH = '/compensation/report';
export const SIMULATOR_PATH = '/compensation/simulator';
export const REFERRERS_PATH = '/compensation/referrers';
export const STATEMENTS_PATH = '/compensation/statements';
export const PAYOUTS_PATH = '/compensation/payouts';
export const PORTAL_PATH = '/compensation/portal';

export const AGREEMENT_TYPES: readonly CmpAgreementType[] = ['FeeForService', 'Salaried', 'Lease', 'Hybrid'];
export const AGREEMENT_STATUSES: readonly CmpAgreementStatus[] = ['Draft', 'Active', 'Superseded', 'Ended'];
export const SETTLEMENT_METHODS: readonly CmpSettlementMethod[] = ['BankTransfer', 'Cash', 'Cheque'];
export const CHARGE_KINDS: readonly CmpFixedChargeKind[] = ['Rent', 'Secretary', 'Other'];

/** نقش‌های قاعده قرارداد؛ فروشنده با کمیسیون فروش (AP-CMP-02)؛ معرف با صفحه معرفان (C-AGENT-1377). */
export const RULE_ROLES: readonly CmpShareRole[] = [
  'Primary',
  'Assistant',
  'Technician',
  'Nurse',
  'Supervisor',
  'Seller',
];
export const RULE_SCOPES: readonly CmpRuleScope[] = ['Service', 'Category', 'All'];

/** مبناها (NetOfCosts با کسر هزینه — US-CMP-013). */
export const RULE_BASES: readonly CmpShareBasis[] = [
  'NetOfDiscount',
  'Gross',
  'ProfessionalComponent',
  'TechnicalComponent',
  'NetOfCosts',
];

/** روش‌ها (پلکانی — AC-CMP-012.2). */
export const RULE_METHODS: readonly CmpShareMethod[] = ['Percent', 'FixedPerCase', 'FixedPerUnit', 'Tiered'];
export const BASES: readonly CmpCompensationBasis[] = ['Accrual', 'Cash'];
export const STATEMENT_PERIODS: readonly CmpStatementPeriod[] = ['Monthly', 'Biweekly', 'Weekly'];
export const COMMISSION_TIMINGS: readonly CmpCommissionTiming[] = ['PerSession', 'AtSale'];
export const LINE_STATUSES: readonly CmpShareEntryStatus[] = [
  'Earned',
  'PendingCollection',
  'Unassigned',
  'NeedsReview',
];
export const LINE_SOURCES: readonly CmpShareSourceKind[] = [
  'Service',
  'PackageSession',
  'PackageExpiry',
  'PackageSale',
  'Membership',
  'ProductSale',
];
export const ENTRY_KINDS: readonly CmpShareEntryKind[] = [
  'Share',
  'Recalculation',
  'Reversal',
  'Manual',
  'CostAdjustment',
  'Collection',
  'Guarantee',
  'Cap',
];

/**
 * پلکان‌ها از متن «آستانه:درصد» جداشده با ویرگول یا خط تازه (مثلاً <c>0:30، 500000000:40</c>)؛ نامعتبر ← <c>null</c>.
 */
export function parseTiers(text: string): { from: number; rateBasisPoints: number }[] | null {
  const parts = latinDigits(text)
    .split(/[,\n\u060c]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const tiers = parts.map((part) => {
    const [from, rate] = part.split(':').map((value) => Number(value.trim()));
    return { from: from ?? Number.NaN, rateBasisPoints: toBasisPoints(rate ?? Number.NaN) };
  });
  const valid =
    tiers.length > 0 &&
    tiers.every(
      (tier) =>
        Number.isFinite(tier.from) &&
        tier.from >= 0 &&
        tier.rateBasisPoints >= 0 &&
        tier.rateBasisPoints <= 10_000,
    );
  return valid && tiers.some((tier) => tier.from === 0)
    ? tiers.sort((left, right) => left.from - right.from)
    : null;
}

/** متن پلکان‌ها برای ویرایش. */
export function tiersText(
  tiers: readonly { from: { amount: number | string }; rateBasisPoints: number | string }[],
): string {
  return tiers
    .map((tier) => `${Number(tier.from.amount)}:${fromBasisPoints(tier.rateBasisPoints) ?? 0}`)
    .join(', ');
}
export const REPORT_GROUPS: readonly CmpShareReportGroup[] = ['Provider', 'Service', 'Branch', 'Period'];
export const BEARING_KINDS = ['automatic', 'coupon', 'manualBySelf', 'manualByOthers', 'rounding'] as const;

/** ویرگول فارسی (جداکننده فهرست نمایشی). */
export const LIST_SEPARATOR = `${String.fromCharCode(0x060c)} `;

export const compensationKeys = {
  agreements: (tenantId: string | undefined) => ['compensation', 'agreements', tenantId] as const,
  agreement: (tenantId: string | undefined, id: string) =>
    ['compensation', 'agreements', tenantId, id] as const,
  lines: (tenantId: string | undefined) => ['compensation', 'lines', tenantId] as const,
  line: (tenantId: string | undefined, id: string) => ['compensation', 'lines', tenantId, id] as const,
  entries: (tenantId: string | undefined) => ['compensation', 'entries', tenantId] as const,
  report: (tenantId: string | undefined) => ['compensation', 'report', tenantId] as const,
  providers: (tenantId: string | undefined) => ['compensation', 'providers', tenantId] as const,
  referrers: (tenantId: string | undefined) => ['compensation', 'referrers', tenantId] as const,
  commissions: (tenantId: string | undefined) => ['compensation', 'commissions', tenantId] as const,
  statements: (tenantId: string | undefined) => ['compensation', 'statements', tenantId] as const,
  statement: (tenantId: string | undefined, id: string) =>
    ['compensation', 'statements', tenantId, id] as const,
  payouts: (tenantId: string | undefined) => ['compensation', 'payouts', tenantId] as const,
  portal: (tenantId: string | undefined) => ['compensation', 'portal', tenantId] as const,
  profitability: (tenantId: string | undefined) => ['compensation', 'profitability', tenantId] as const,
};

/** رنگ وضعیت صورت‌وضعیت (SM-Statement). */
export function statementTone(status: string): StatusTone {
  switch (status) {
    case 'Paid':
    case 'Acknowledged':
      return 'active';
    case 'Disputed':
      return 'error';
    case 'Draft':
      return 'inactive';
    default:
      return 'pending';
  }
}

/** همه کلیدهای وابسته به دفتر (پس از اصلاح، بازمحاسبه یا فعال‌سازی قرارداد). */
export const ledgerScopes = (tenantId: string | undefined) => [
  compensationKeys.lines(tenantId),
  compensationKeys.entries(tenantId),
  compensationKeys.report(tenantId),
];

/** رنگ وضعیت نسخه قرارداد (SM-CompensationAgreement). */
export function agreementTone(status: CmpAgreementStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Draft':
      return 'pending';
    default:
      return 'inactive';
  }
}

/** رنگ وضعیت ردیف و قلم (SM-ShareLedgerEntry). */
export function lineTone(status: CmpShareEntryStatus): StatusTone {
  switch (status) {
    case 'Earned':
      return 'active';
    case 'NeedsReview':
      return 'error';
    default:
      return 'pending';
  }
}

/** درصد نمایشی ← Basis Point (۴۰ ← ۴۰۰۰). */
export const toBasisPoints = (percent: number) => Math.round(percent * 100);

/** Basis Point ← درصد نمایشی. */
export const fromBasisPoints = (basisPoints: number | string | null | undefined) =>
  basisPoints === null || basisPoints === undefined ? undefined : Number(basisPoints) / 100;

export interface ProviderOption {
  id: string;
  displayName: string;
}

/** ارائه‌دهندگان فعال با جستجوی سروری (GET compensation/providers?q=) و نگه‌داشت نام‌های دیده‌شده. */
export function useProviderOptions() {
  const { tenantApi, session } = useTenantSession();
  const [search, setSearch] = useState('');
  const seen = useRef(new Map<string, string>());
  const query = useQuery({
    queryKey: [...compensationKeys.providers(session?.tenantId), search] as const,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/providers', {
          params: { query: { q: search.trim() || undefined, limit: 20 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const options: ProviderOption[] = (query.data?.items ?? []).map((item) => ({
    id: item.id,
    displayName: item.displayName,
  }));
  for (const option of options) {
    seen.current.set(option.id, option.displayName);
  }
  return {
    ...query,
    options,
    search: setSearch,
    name: (id: string | null | undefined) => (id ? (seen.current.get(id) ?? '') : ''),
  };
}

/** انتخاب ارائه‌دهنده (Autocomplete با جستجوی سروری). */
export function ProviderPicker({
  value,
  onChange,
  labelKey,
  testId,
  disabled,
  error,
  helperText,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  labelKey: string;
  testId: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  const { t } = useTranslation('cmp');
  const providers = useProviderOptions();
  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      options={providers.options.map((option) => option.id)}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(id) => providers.name(id) || id.slice(0, 8)}
      filterOptions={(options) => options}
      onInputChange={(_, text, reason) => {
        if (reason === 'input') {
          providers.search(text);
        }
      }}
      loading={providers.isFetching}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(labelKey)}
          placeholder={t('cmp:form.provider_search')}
          error={error}
          helperText={helperText}
          slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': testId } }}
        />
      )}
    />
  );
}

/** متن محاسبه یک ردیف (AC-CMP-020.1): «۴۰٪ از مبنا»، «مبلغ ثابت» یا علت صفر. */
export function useCalculationText() {
  const { t } = useTranslation('cmp');
  const money = useMoneyFormatter();
  return (row: CmpShareEntryView): string => {
    if (row.beneficiary === 'Center' || row.kind === 'Manual') {
      return '';
    }
    if (row.note === 'no_agreement' || row.note === 'no_rule' || row.note === 'salaried') {
      return t(`cmp:line_detail.${row.note}`);
    }
    if (row.method === 'Percent' && row.rateBasisPoints !== null && row.rateBasisPoints !== undefined) {
      return t('cmp:line_detail.percent', {
        rate: faDigits(Number(row.rateBasisPoints) / 100),
        base: money(Number(row.base.amount)),
      });
    }
    if (row.method) {
      return t(`cmp:method.${row.method}`);
    }
    return '';
  };
}
